/**
 * Geometrie der Lane: Wegstrecke, Position entlang des Pfades und Begrenzungen
 * (Masterprompt Abschnitte 31, 34, 43).
 */

import { LANE_BOUNDS, LANE_HALF_WIDTH, LANE_PATH } from '@dueline/game-data';
import { clamp, distance, type Vec2 } from '@dueline/shared';

/** Gesamtlaenge des Lane-Pfades in Welteinheiten. */
export function getLanePathLength(path: readonly Vec2[] = LANE_PATH): number {
  let total = 0;
  for (let i = 1; i < path.length; i += 1) {
    const previous = path[i - 1];
    const current = path[i];
    if (!previous || !current) continue;
    total += distance(previous, current);
  }
  return total;
}

/**
 * Position auf dem Lane-Pfad nach einer zurueckgelegten Strecke.
 * Werte ausserhalb des Pfades werden auf Anfang bzw. Ende begrenzt.
 */
export function getPositionAlongLane(travelled: number, path: readonly Vec2[] = LANE_PATH): Vec2 {
  const first = path[0];
  const last = path[path.length - 1];
  if (!first || !last) return { x: 0, y: 0 };
  if (travelled <= 0) return { ...first };

  let remaining = travelled;
  for (let i = 1; i < path.length; i += 1) {
    const from = path[i - 1];
    const to = path[i];
    if (!from || !to) continue;
    const segmentLength = distance(from, to);
    if (remaining <= segmentLength) {
      const t = segmentLength === 0 ? 0 : remaining / segmentLength;
      return {
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t,
      };
    }
    remaining -= segmentLength;
  }
  return { ...last };
}

/** Ergebnis der Projektion eines Punktes auf den Lane-Pfad. */
export interface LaneProjection {
  /** Zurueckgelegte Strecke am naechstgelegenen Punkt des Pfades. */
  travelled: number;
  /** Der naechstgelegene Punkt auf der Mittellinie. */
  position: Vec2;
  /** Senkrechter Abstand zur Mittellinie. */
  distance: number;
}

/**
 * Sucht den Punkt auf der Lane, der einer beliebigen Position am naechsten liegt.
 *
 * Wozu das gebraucht wird:
 * Ein Creep, der einem Hero hinterhergelaufen ist, steht irgendwo neben der Lane.
 * Um weiterzulaufen, muss er wissen, WIE WEIT er auf der Lane eigentlich schon ist -
 * gemessen an seiner jetzigen Position, nicht an der Stelle, an der er die Lane
 * einmal verlassen hat.
 *
 * Ohne diese Berechnung liesse sich ein Creep endlos hin und her ziehen, ohne dass
 * er je Fortschritt macht.
 */
export function projectOntoLane(position: Vec2, path: readonly Vec2[] = LANE_PATH): LaneProjection {
  let travelledSoFar = 0;
  let best: LaneProjection = {
    travelled: 0,
    position: path[0] ? { ...path[0] } : { x: 0, y: 0 },
    distance: Number.POSITIVE_INFINITY,
  };

  for (let i = 1; i < path.length; i += 1) {
    const from = path[i - 1];
    const to = path[i];
    if (!from || !to) continue;

    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const segmentLengthSquared = dx * dx + dy * dy;
    const segmentLength = Math.sqrt(segmentLengthSquared);

    // Wie weit entlang DIESES Abschnitts liegt der naechste Punkt? 0 = Anfang,
    // 1 = Ende. Werte ausserhalb werden auf den Abschnitt begrenzt.
    const t =
      segmentLengthSquared === 0
        ? 0
        : clamp(
            ((position.x - from.x) * dx + (position.y - from.y) * dy) / segmentLengthSquared,
            0,
            1,
          );

    const candidate: Vec2 = { x: from.x + dx * t, y: from.y + dy * t };
    const candidateDistance = distance(position, candidate);

    if (candidateDistance < best.distance) {
      best = {
        travelled: travelledSoFar + segmentLength * t,
        position: candidate,
        distance: candidateDistance,
      };
    }

    travelledSoFar += segmentLength;
  }

  return best;
}

/** Position und Ausrichtung an einer Stelle des Lane-Pfades. */
export interface LaneFrame {
  position: Vec2;
  /** Laufrichtung, Laenge 1. */
  forward: Vec2;
  /** Senkrecht dazu, Laenge 1 - fuer den seitlichen Versatz einer Einheit. */
  normal: Vec2;
}

/**
 * Liefert Position und Ausrichtung an einer Stelle des Pfades.
 *
 * Damit kann eine Einheit ihren seitlichen Abstand zur Mittellinie behalten,
 * auch wenn die Lane eine Kurve macht - sonst wuerden alle Creeps zu einer
 * einzigen Linie zusammenlaufen.
 */
export function getLaneFrame(travelled: number, path: readonly Vec2[] = LANE_PATH): LaneFrame {
  const position = getPositionAlongLane(travelled, path);
  const ahead = getPositionAlongLane(travelled + 1, path);

  let dx = ahead.x - position.x;
  let dy = ahead.y - position.y;
  let length = Math.hypot(dx, dy);

  if (length < 1e-6) {
    // Am Ende des Pfades gibt es kein "voraus" mehr - dann nehmen wir die
    // Richtung von kurz davor.
    const behind = getPositionAlongLane(travelled - 1, path);
    dx = position.x - behind.x;
    dy = position.y - behind.y;
    length = Math.hypot(dx, dy);
  }

  if (length < 1e-6) {
    return { position, forward: { x: 1, y: 0 }, normal: { x: 0, y: 1 } };
  }

  const forward: Vec2 = { x: dx / length, y: dy / length };
  return { position, forward, normal: { x: -forward.y, y: forward.x } };
}

/**
 * Begrenzt einen Punkt auf die GESAMTE Lane.
 *
 * Gebraucht ueberall dort, wo es nicht um die Bewegung des Heroes geht -
 * vor allem beim Zielen: Ueber die eigene Haelfte hinaus zu zielen ist
 * erlaubt und noetig, dorthin zu LAUFEN nicht. Fuer die Bewegung gibt es
 * clampToSideBounds in shared-lane.ts.
 *
 * Hiess bis zum 08.10.2026 clampToHeroBounds und war damals beides
 * zugleich - in der alten Lane gab es nur eine Grenze.
 */
export function clampToLaneBounds(position: Vec2): Vec2 {
  return begrenzeQuerZurLane({
    x: clamp(position.x, LANE_BOUNDS.minX, LANE_BOUNDS.maxX),
    y: position.y,
  });
}

/** Liegt die Position innerhalb der gesamten Lane? */
export function isInsideLaneBounds(position: Vec2): boolean {
  return (
    position.x >= LANE_BOUNDS.minX &&
    position.x <= LANE_BOUNDS.maxX &&
    projectOntoLane(position).distance <= LANE_HALF_WIDTH
  );
}

/**
 * Wo die Mitte der Lane bei einem x-Wert liegt.
 *
 * Geht nur, weil der Pfad in x stetig waechst - jede senkrechte Linie
 * schneidet ihn genau einmal. Ein Test in lane.test.ts haelt das fest.
 */
export function laneMitteBeiX(x: number, path: readonly Vec2[] = LANE_PATH): number {
  const erster = path[0];
  const letzter = path[path.length - 1];
  if (!erster || !letzter) return 0;
  if (x <= erster.x) return erster.y;
  if (x >= letzter.x) return letzter.y;
  for (let i = 1; i < path.length; i += 1) {
    const von = path[i - 1];
    const bis = path[i];
    if (!von || !bis || x > bis.x) continue;
    const anteil = bis.x === von.x ? 0 : (x - von.x) / (bis.x - von.x);
    return von.y + (bis.y - von.y) * anteil;
  }
  return letzter.y;
}

/**
 * Haelt einen Punkt hoechstens eine halbe Lane-Breite neben dem Lane-Pfad
 * (Produktentscheidung vom 11.10.2026).
 *
 * Bis dahin war die Grenze ein gerader Streifen (y von -17 bis +17),
 * die Lane aber biegt sich um bis zu sechs Einheiten. Gezeichnet wird sie
 * mit Mauern entlang der Kurve - an den Kurven lief der Hero durch die eine
 * Mauer hindurch und kam an die andere nicht heran. Jetzt folgt die Grenze
 * der Kurve.
 *
 * Verschoben wird NUR quer, das x bleibt. Wuerde der Punkt einfach auf den
 * naechsten Lane-Punkt zugezogen, bewegte er sich an einer schraegen
 * Stelle auch ein Stueck nach vorn - und koennte so ueber die Leash
 * rutschen, die gerade erst das x begrenzt hat.
 */
export function begrenzeQuerZurLane(position: Vec2, path: readonly Vec2[] = LANE_PATH): Vec2 {
  if (projectOntoLane(position, path).distance <= LANE_HALF_WIDTH) return position;
  // Zwischen der Mitte (sicher innen) und dem Punkt (aussen) die Kante
  // suchen. Halbieren reicht: 30 Schritte sind genauer als ein Pixel.
  // Den aeusseren Startpunkt nah heranholen - drei Lane-Breiten sind
  // sicher draussen. Sonst kostete ein absurd weiter Punkt Genauigkeit.
  let innen = laneMitteBeiX(position.x, path);
  let aussen = clamp(position.y, innen - LANE_HALF_WIDTH * 6, innen + LANE_HALF_WIDTH * 6);
  for (let i = 0; i < 30; i += 1) {
    const mitte = (innen + aussen) / 2;
    if (projectOntoLane({ x: position.x, y: mitte }, path).distance <= LANE_HALF_WIDTH) {
      innen = mitte;
    } else {
      aussen = mitte;
    }
  }
  return { x: position.x, y: innen };
}
